import { useEffect, useRef } from "react"

import { useOIOAgent } from "./useOIOAgent"
import { useOIORewriteAgent } from "./useOIORewriteAgent"

/**
 * When the rewrite agent completes and updated_oio_draft is set,
 * push the new draft into the oio agent's shared state so DocumentViewer
 * re-renders without a full page reload.
 *
 * Important: this hook uses the `setState` returned by `useOIOAgent` rather
 * than calling `useCoAgent("oio")` a second time.  Creating two useCoAgent
 * subscriptions for the same agent name in the same render tree can cause
 * double-render cycles and state conflicts.
 */
export function useRewriteCompletion(): void {
  const { rewrite_status, updated_oio_draft, artifact_blob_url, draft_revision } = useOIORewriteAgent()
  const { setState: setOIOState } = useOIOAgent()

  // CRITICAL: CopilotKit's useCoAgent returns a NEW setState function every
  // render, so keeping setOIOState in the deps re-ran this effect every
  // render — and for a completed-rewrite case (rewrite_status "done" +
  // updated_oio_draft set) it called setOIOState on every render, each call
  // re-rendering the agent's subscribers, minting a new setState, re-running
  // the effect: an infinite render loop (~115/s, the Analysis-tab freeze).
  // Push each unique draft EXACTLY ONCE via a ref latch, and depend only on
  // the actual draft value — never on the unstable setState reference.
  const pushedRef = useRef<string | null>(null)
  const setOIOStateRef = useRef(setOIOState)
  setOIOStateRef.current = setOIOState
  useEffect(() => {
    if (rewrite_status !== "done" || !updated_oio_draft) return
    const key = `${updated_oio_draft.length}:${draft_revision ?? ""}:${updated_oio_draft.slice(-64)}`
    if (pushedRef.current === key) return
    pushedRef.current = key
    setOIOStateRef.current((prev) => ({
      thread_id: prev?.thread_id ?? "",
      draft_status: prev?.draft_status ?? "draft_not_started",
      completed_stages: prev?.completed_stages ?? [],
      current_stage: prev?.current_stage ?? null,
      current_substage: prev?.current_substage ?? null,
      oio_draft: updated_oio_draft,
      artifact_blob_url: artifact_blob_url ?? null,
      draft_revision: draft_revision ?? null,
      findings_summary_json: prev?.findings_summary_json ?? null,
    }))
  }, [artifact_blob_url, draft_revision, rewrite_status, updated_oio_draft])
}
